
import { Vue, Component, Watch } from 'vue-property-decorator'
import Building, { columns, productTypes } from '@/types/building'
import User from '@/types/user'
import Column, { FilterOption } from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import BuildingBulkUpdater from '@/components/listview/BuildingBulkUpdater.vue'
import ColumnOptions from '@/components/listview/ColumnOptions.vue'
import PageHeader from '@/components/base/PageHeader.vue'
import BuildingBulkCreate from '@/components/building/BuildingBulkCreate.vue'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { SearchableColumnFilter, BuildingBulkCreate, PageHeader, ColumnOptions, BuildingBulkUpdater, ActiveFilters }
})

export default class Buildings extends Vue {
  settings = {
    routeNames: { show: 'buildingShow', create: 'buildingCreate', edit: 'buildingEdit' },
    routeParamKey: 'buildingId',
    permissionNames: { show: 'buildings.view', edit: 'buildings.edit', delete: 'buildings.delete' },
    functionNames: { getList: 'getBuildingList' },
    getterNames: { list: 'buildingList', loading: 'loadingBuilding' },
    localStorageName: 'buildingTableSettings'
  }

  sortOrder = { column: '', order: '' }
  conditions = {
    deliveryDate: ['not null']
  }

  localSettings = ls.get(this.settings.localStorageName)
  tableSettings:TableSettings = TableSettings.from(this.localSettings, this.conditions)

  columns = columns
  productTypes = productTypes
  selectedRowKeys:number[] = []
  filtersLoaded = false

  mounted () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
    this.onTableSettingsChange()
    this.HookCustomColumFilterEvents()
  }

  filterVisible = false
  HookCustomColumFilterEvents () : void {
    this.columns.forEach((column:Column) => {
      if (column.scopedSlots?.filterDropdown !== undefined) {
        column.onFilterDropdownVisibleChange = (visible: boolean) => {
          this.filterVisible = visible
        }
      }
    })
  }

  reloadList () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}, archived:boolean, unrelated:boolean) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = filters
    this.tableSettings.archived = archived
    this.tableSettings.unrelated = unrelated
    this.reloadList()
  }

  toggleInactiveItems (event: {target: { checked: boolean } }): void {
    this.tableSettings.archived = event.target.checked
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  toggleUnrelatedItems (event: {target: { checked: boolean } }) : void {
    this.tableSettings.unrelated = event.target.checked
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  get activeColumns (): Column[] {
    return this.columns.filter(col => !this.$store.getters.InactiveBuildingColumns.includes(col.key))
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          if (!this.selectedRowKeys.length) {
            const keyboard = window.event as KeyboardEvent
            if (can(this.settings.permissionNames.edit)) {
              const route = this.$router.resolve({ name: this.settings.routeNames.edit, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
              window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
            } else if (can(this.settings.permissionNames.show)) {
              const route = this.$router.resolve({ name: this.settings.routeNames.show, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
              window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
            }
          } else {
            this.selectedRowKeys.includes(rowData.id) ? this.selectedRowKeys.splice(this.selectedRowKeys.indexOf(rowData.id), 1) : this.selectedRowKeys.push(rowData.id)
          }
        }
      }
    }
  }

  onSelectChange (selectedRowKeys:number[]) : void {
    this.selectedRowKeys = selectedRowKeys
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  get selectedBuildings () : Building[] {
    const selectedBuildings:Building[] = []

    this.itemList.data.forEach((building:Building) => {
      if (this.selectedRowKeys.includes(building.id)) {
        selectedBuildings.push(building)
      }
    })

    return selectedBuildings
  }

  get itemList () : { data: Building[], meta: { per_page:string, total:number }, filters: { [key:string]: FilterOption[] } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get filters () : { [key:string]: FilterOption[] } {
    return this.itemList.filters || {}
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Bouwnummer ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  get navigationCollapsed () : boolean {
    return this.$store.getters.navigationCollapsed
  }

  get screenWidth () : number {
    return screen.width
  }

  columnFilterKeys : { [key:string]: string } = {
    'project.id': 'projects',
    buildingblock_id: 'building_blocks'
  }

  @Watch('filters', { immediate: true, deep: true })
  onFiltersChange () : void {
    this.columns.forEach((column:Column) => {
      const filterKey = this.columnFilterKeys[column.key] || null
      if (filterKey && this.filters && filterKey in this.filters) {
        column.filters = this.filters[filterKey]
      }
    })
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
